import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Globe, Users, UserCog, Trophy, Plus, Loader2, Save, X,
  ExternalLink, Settings, Power,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramsManager = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', sport: 'Basketball', primary_color: '#06b6d4', admin_email: '' });
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);

    const [statsRes, progsRes] = await Promise.all([
      fetch(`${API_URL}/api/programs/platform/stats`, { headers }),
      fetch(`${API_URL}/api/programs/platform/programs?${params}`, { headers }),
    ]);
    if (statsRes.ok) setStats(await statsRes.json());
    if (progsRes.ok) { const d = await progsRes.json(); setPrograms(d.programs || []); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, token]);

  useEffect(() => { load(); }, [load]);

  const createProgram = async () => {
    setCreating(true);
    try {
      const slug = createForm.slug || createForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const res = await fetch(`${API_URL}/api/programs/platform/programs?admin_email=${encodeURIComponent(createForm.admin_email || '')}`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: createForm.name, slug, sport: createForm.sport, primary_color: createForm.primary_color }),
      });
      if (res.ok) {
        toast.success('Program created');
        setShowCreateForm(false);
        setCreateForm({ name: '', slug: '', sport: 'Basketball', primary_color: '#06b6d4', admin_email: '' });
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to create'); }
    setCreating(false);
  };

  const toggleStatus = async (programId, currentStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    const res = await fetch(`${API_URL}/api/programs/platform/programs/${programId}/status?status=${newStatus}`, {
      method: 'PUT', headers,
    });
    if (res.ok) {
      toast.success(`Program ${newStatus}`);
      load();
    } else toast.error('Failed');
  };

  if (loading && !stats) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div data-testid="programs-manager">
      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6" data-testid="platform-stats">
          {[
            { label: 'Programs', value: stats.total_programs, icon: Globe, color: 'text-cyan-400' },
            { label: 'Teams', value: stats.total_teams, icon: Users, color: 'text-emerald-400' },
            { label: 'Users', value: stats.total_users, icon: UserCog, color: 'text-amber-400' },
            { label: 'Games', value: stats.total_games, icon: Trophy, color: 'text-red-400' },
          ].map(s => (
            <Card key={s.label} className="bg-rooam-gray-medium border-rooam-gray-light" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                  <span className="text-zinc-500 text-[10px] font-medium truncate">{s.label}</span>
                </div>
                <span className="text-white font-black text-xl">{s.value}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search programs..."
          className="bg-rooam-gray-medium border-rooam-gray-light text-white text-xs flex-1 min-w-[120px] max-w-[200px]"
          data-testid="search-programs-input"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-rooam-gray-medium border border-zinc-700 text-zinc-300 text-xs rounded-md px-2 py-2"
          data-testid="status-filter"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <Button onClick={() => setShowCreateForm(true)} className="text-xs gap-1.5 bg-rooam-red hover:bg-red-700 ml-auto shrink-0" data-testid="create-program-btn">
          <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Create Program</span><span className="sm:hidden">New</span>
        </Button>
      </div>

      {/* Create Program Form */}
      {showCreateForm && (
        <Card className="bg-rooam-gray-medium border-rooam-gray-light mb-4" data-testid="create-program-form">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-white text-sm font-bold flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /> New Program</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="Program Name *" className="bg-rooam-gray-dark border-rooam-gray-light text-white text-xs" data-testid="program-name-input" />
              <Input value={createForm.slug} onChange={e => setCreateForm(p => ({ ...p, slug: e.target.value }))} placeholder="Slug (auto-generated)" className="bg-rooam-gray-dark border-rooam-gray-light text-white text-xs" data-testid="program-slug-input" />
              <Input value={createForm.sport} onChange={e => setCreateForm(p => ({ ...p, sport: e.target.value }))} placeholder="Sport" className="bg-rooam-gray-dark border-rooam-gray-light text-white text-xs" />
              <Input value={createForm.admin_email} onChange={e => setCreateForm(p => ({ ...p, admin_email: e.target.value }))} placeholder="First Admin Email" className="bg-rooam-gray-dark border-rooam-gray-light text-white text-xs" data-testid="program-admin-email-input" />
              <div className="flex items-center gap-2">
                <input type="color" value={createForm.primary_color} onChange={e => setCreateForm(p => ({ ...p, primary_color: e.target.value }))} className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent" />
                <span className="text-zinc-500 text-xs">Brand Color</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={createProgram} disabled={!createForm.name || creating} className="text-xs gap-1.5 bg-rooam-red hover:bg-red-700" data-testid="create-program-submit">
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Create
              </Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Programs List */}
      <div className="space-y-2">
        {programs.map(p => (
          <Card key={p.id} className={`bg-rooam-gray-medium border-rooam-gray-light ${p.status === 'suspended' ? 'opacity-60' : ''}`} data-testid={`program-row-${p.id}`}>
            <CardContent className="p-3 sm:p-4">
              {/* Color dot + name */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-10 rounded-full shrink-0" style={{ backgroundColor: p.primary_color || '#06b6d4' }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">{p.name}</span>
                    <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ${
                      p.status === 'active' ? 'text-emerald-400 border-emerald-800' :
                      p.status === 'suspended' ? 'text-red-400 border-red-800' : 'text-zinc-500 border-zinc-700'
                    }`}>{p.status || 'active'}</Badge>
                  </div>
                  <div className="text-[10px] text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="font-mono">/{p.slug}</span>
                    <span>{p.team_count} teams</span>
                    <span>{p.member_count} members</span>
                    <span>{p.media_count} media</span>
                    {p.admins?.[0] && <span>Admin: {p.admins[0].name || p.admins[0].email}</span>}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-wrap ml-6">
                <a
                  href={`/${p.slug}/dashboard`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors border border-zinc-700"
                  data-testid={`preview-${p.id}`}
                >
                  <ExternalLink className="w-3 h-3" /> Preview
                </a>
                <a
                  href={`/${p.slug}/admin`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors border border-zinc-700"
                  data-testid={`manage-${p.id}`}
                >
                  <Settings className="w-3 h-3" /> Manage
                </a>
                <button
                  onClick={() => toggleStatus(p.id, p.status || 'active')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium border transition-colors ${
                    p.status === 'suspended'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-800 hover:bg-emerald-500/20'
                      : 'bg-red-500/10 text-red-400 border-red-800 hover:bg-red-500/20'
                  }`}
                  data-testid={`toggle-status-${p.id}`}
                >
                  <Power className="w-3 h-3" /> {p.status === 'suspended' ? 'Activate' : 'Suspend'}
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
        {programs.length === 0 && (
          <p className="text-zinc-600 text-sm text-center py-8">No programs found</p>
        )}
      </div>
    </div>
  );
};

export default ProgramsManager;
